import { LIST_PRODUCT_FEATURES } from "./product-features-lists";
import { OUTREACH_PRODUCT_FEATURES } from "./product-features-outreach";
import { PLATFORM_PRODUCT_FEATURES } from "./product-features-platform";

/**
 * The product feature catalog (PREC-4329, usage analytics 2/10): the one list of features that
 * every usage event names and the usage dashboard lists, including features nobody touched.
 *
 * `FeaturesCodes` cannot be this list: it is an entitlement list, several of its codes are rollout
 * switches, and many real features have no code. An entry links to the code that gates it through
 * its optional `featureCode`.
 *
 * Who names a feature:
 * - every web route, through `ROUTE_FEATURES` (`apps/web/src/routes/routeFeatures.ts`), a `Record`
 *   over `ROUTE_CONFIG`, so a new route does not compile until it is mapped;
 * - every MCP tool, through `MCP_TOOL_FEATURES` (`mcp-tool-features.ts`), whose keys are the only
 *   names the API's `MappedMcpServer` lets a tool register under;
 * - the rest are listed in `UNMAPPED_BY_DESIGN` (`product-features-unmapped.ts`) with the reason
 *   no route or tool names them.
 */
export const PRODUCT_FEATURES = [
	...LIST_PRODUCT_FEATURES,
	...OUTREACH_PRODUCT_FEATURES,
	...PLATFORM_PRODUCT_FEATURES,
] as const;

export type ProductFeature = (typeof PRODUCT_FEATURES)[number];

export type ProductFeatureKey = ProductFeature["key"];

export const PRODUCT_FEATURE_KEYS: readonly ProductFeatureKey[] = PRODUCT_FEATURES.map(
	(feature) => feature.key
);

const PRODUCT_FEATURE_KEY_SET: ReadonlySet<string> = new Set(PRODUCT_FEATURE_KEYS);

/** Whether a stored or received string is a key of the catalog (an event names nothing else). */
export function isProductFeatureKey(value: string): value is ProductFeatureKey {
	return PRODUCT_FEATURE_KEY_SET.has(value);
}
